import { OAuthApplicationFormValues } from '../../../../types';
import { isProduction } from '@/shared/constants';
import { OAuthApplicationFormSectionKey } from './steps';
import { isValidRedirectUri } from './validation';

export type RequiredOAuthApplicationField =
    | 'iconUrl'
    | 'name'
    | 'slug'
    | 'shortDescription'
    | 'redirectUris'
    | 'scopes';

export const requiredOAuthApplicationFieldDetails: Record<
    RequiredOAuthApplicationField,
    { fieldId: string; label: string; sectionKey: OAuthApplicationFormSectionKey }
> = {
    iconUrl: { fieldId: 'oauth-application-field-icon', label: 'Application Icon', sectionKey: 'metadata' },
    name: { fieldId: 'oauth-application-field-name', label: 'Application Name', sectionKey: 'metadata' },
    slug: { fieldId: 'oauth-application-field-slug', label: 'Application Slug', sectionKey: 'metadata' },
    shortDescription: {
        fieldId: 'oauth-application-field-short-description',
        label: 'Short Description',
        sectionKey: 'metadata',
    },
    redirectUris: {
        fieldId: 'oauth-application-field-redirect-uri',
        label: 'Redirect URI',
        sectionKey: 'settings',
    },
    scopes: { fieldId: 'oauth-application-field-scopes', label: 'Scopes', sectionKey: 'scopes' },
};

export const requiredOAuthApplicationFieldOrder: RequiredOAuthApplicationField[] = [
    'iconUrl',
    'name',
    'slug',
    'shortDescription',
    'redirectUris',
    'scopes',
];

export interface OAuthApplicationFormReadiness {
    metadataComplete: boolean;
    redirectUrisComplete: boolean;
    missingRequiredFields: RequiredOAuthApplicationField[];
    previewReady: boolean;
}

interface OAuthApplicationFormReadinessOptions {
    requireApplicationIcon?: boolean;
}

export function getOAuthApplicationFormReadiness(
    values: Partial<OAuthApplicationFormValues>,
    options: OAuthApplicationFormReadinessOptions = {},
): OAuthApplicationFormReadiness {
    const missingRequiredFields: RequiredOAuthApplicationField[] = [];
    const iconComplete = !(options.requireApplicationIcon ?? isProduction) || Boolean(values.iconUrl?.trim());
    const nameComplete = Boolean(values.name?.trim() && values.name.trim().length >= 3);
    const slugComplete = Boolean(values.slug?.trim() && /^[a-z0-9-]+$/.test(values.slug));
    const shortDescriptionComplete = Boolean(values.shortDescription?.trim());
    const redirectUrisComplete = Boolean(
        values.redirectUris?.length && values.redirectUris.every((redirectUri) => isValidRedirectUri(redirectUri)),
    );
    const scopesComplete = Boolean(values.scopes?.length);

    const completeFields: Record<RequiredOAuthApplicationField, boolean> = {
        iconUrl: iconComplete,
        name: nameComplete,
        slug: slugComplete,
        shortDescription: shortDescriptionComplete,
        redirectUris: redirectUrisComplete,
        scopes: scopesComplete,
    };

    requiredOAuthApplicationFieldOrder.forEach((field) => {
        if (!completeFields[field]) {
            missingRequiredFields.push(field);
        }
    });

    return {
        metadataComplete: iconComplete && nameComplete && slugComplete && shortDescriptionComplete,
        redirectUrisComplete,
        missingRequiredFields,
        previewReady: missingRequiredFields.length === 0,
    };
}
